/* eslint-disable no-nested-ternary, camelcase */
import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// Components
import { SectionCard, FieldSearchSelect, Chip, DateRangePopover } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useGetSizingSimulationResultsQuery } from "state/sizingOptimization/sizingOptimizationApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";
import { useGetMetersByCommunityIdQuery } from "state/meters/metersApiSlice";

import KeyMetrics from "./KeyMetrics";
import Charts from "./Charts";

const SIMULATION_TASK_TYPE = "SIZING_SIMULATION";

function Results({ communityId }) {
  const { t } = useTranslation(["sizing"]);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [selectedMember, setSelectedMember] = useState("all");
  const [memberOptions, setMemberOptions] = useState([]);
  const [availableStartDate, setAvailableStartDate] = useState(null);
  const [availableEndDate, setAvailableEndDate] = useState(null);

  const activeSimulationTasks = useSelector(selectActiveTasksByType(SIMULATION_TASK_TYPE));
  const isSimulationRunning = activeSimulationTasks.length > 0;

  const { data: metersData } = useGetMetersByCommunityIdQuery({ communityId });

  // First API call: Fetch ALL data (without startDate/endDate)
  const { data: initialResults } = useGetSizingSimulationResultsQuery({ communityId });

  // Second API call: Fetch data with filters (once we have start/end date)
  const {
    data: simulationResults,
    isLoading,
    error,
  } = useGetSizingSimulationResultsQuery(
    {
      communityId,
      startDate: dateRange.start,
      endDate: dateRange.end,
      meterId: selectedMember !== "all" ? selectedMember : null,
    },
    {
      skip: !dateRange.start || !dateRange.end,
      refetchOnMountOrArgChange: true,
    }
  );

  // Keep the available date range in sync with the latest full result set.
  useEffect(() => {
    if (!initialResults?.length) return;

    const firstDate = DateTime.fromISO(initialResults[0].datetime_timestamp).toUTC().toISODate();
    const lastDate = DateTime.fromISO(initialResults[initialResults.length - 1].datetime_timestamp)
      .toUTC()
      .toISODate();

    setAvailableStartDate(firstDate);
    setAvailableEndDate(lastDate);

    setDateRange((prev) => {
      const rangeChanged = prev.start !== firstDate || prev.end !== lastDate;
      return rangeChanged ? { start: firstDate, end: lastDate } : prev;
    });
  }, [initialResults]);

  // Populate member options
  useEffect(() => {
    if (metersData?.entities) {
      const options = Object.entries(metersData.entities)
        .filter((entry) => entry[1].type?.toLowerCase() === "cpe")
        .map(([id, meter]) => ({ value: id, label: meter.identifier }));

      setMemberOptions([{ value: "all", label: t("results.controls.allMembers") }, ...options]);
    }
  }, [metersData, t]);

  const handleRangeChange = ({ start, end }) => {
    setDateRange((prev) => {
      if (!start || !end) return prev;
      if (start > end) return prev;
      const finalStartDate =
        availableStartDate && start < availableStartDate ? availableStartDate : start;
      const finalEndDate = availableEndDate && end > availableEndDate ? availableEndDate : end;
      return { start: finalStartDate, end: finalEndDate };
    });
  };

  // Aggregate data if "all" is selected
  const processedData = useMemo(() => {
    if (!simulationResults || simulationResults.length === 0) return [];

    if (selectedMember === "all") {
      const aggregatedMap = {};
      simulationResults.forEach((entry) => {
        const timestamp = entry.datetime_timestamp;
        if (!aggregatedMap[timestamp]) {
          aggregatedMap[timestamp] = {
            datetime_timestamp: timestamp,
            obj_value: entry.obj_value,
            p_gn_total: 0,
            e_bn_total: 0,
            e_g: 0,
            e_sup: 0,
            e_sur: 0,
            e_bat: 0,
            p_gn_new: 0,
            e_bn_new: 0,
            c_ind2pool: entry.c_ind2pool,
          };
        }
        aggregatedMap[timestamp].p_gn_total += entry.p_gn_total ?? 0;
        aggregatedMap[timestamp].e_bn_total += entry.e_bn_total ?? 0;
        aggregatedMap[timestamp].e_g += entry.e_g ?? 0;
        aggregatedMap[timestamp].e_sup += entry.e_sup ?? 0;
        aggregatedMap[timestamp].e_sur += entry.e_sur ?? 0;
        aggregatedMap[timestamp].e_bat += entry.e_bat ?? 0;
        aggregatedMap[timestamp].p_gn_new += entry.p_gn_new ?? 0;
        aggregatedMap[timestamp].e_bn_new += entry.e_bn_new ?? 0;
      });
      return Object.values(aggregatedMap);
    }

    return simulationResults;
  }, [simulationResults, selectedMember]);

  const milpStatus = simulationResults?.length > 0 ? simulationResults[0].milp_status : null;
  const isOptimal = milpStatus && milpStatus.toLowerCase() === "optimal";
  const selectedIdentification =
    memberOptions.find((option) => option.value === selectedMember) || null;

  const action = (
    <div className="flex items-center gap-[10px] flex-wrap">
      {isSimulationRunning && (
        <span className="animate-spin inline-flex">
          <Icon name="progress_activity" style={{ fontSize: 18, color: "var(--accent)" }} />
        </span>
      )}
      {milpStatus && (
        <Chip tone={isOptimal ? "green" : "red"}>
          <Icon name="check_circle" fill style={{ fontSize: 13 }} />
          {milpStatus.toLowerCase()}
        </Chip>
      )}
      <DateRangePopover
        value={{ start: dateRange.start, end: dateRange.end }}
        onChange={handleRangeChange}
      />
      <div className="min-w-[220px]">
        <FieldSearchSelect
          value={selectedMember}
          onChange={(v) => setSelectedMember(v)}
          options={memberOptions}
        />
      </div>
    </div>
  );

  return (
    <SectionCard id="results" icon="insights" title={t("results.title")} action={action}>
      {isSimulationRunning ? (
        <div className="flex items-center gap-2 text-[13px] text-ink-2 py-8 justify-center">
          <span className="animate-spin inline-flex">
            <Icon name="progress_activity" style={{ fontSize: 20, color: "var(--accent)" }} />
          </span>
          {t("results.updating", {
            defaultValue: "A calcular a simulação e a atualizar os resultados...",
          })}
        </div>
      ) : isLoading ? (
        <p className="text-[13px] text-ink-3">{t("results.loading")}</p>
      ) : error ? (
        <p className="text-[13px] text-ink-3">
          {t("results.errors.fetch", { message: error.message })}
        </p>
      ) : simulationResults ? (
        <div className="flex flex-col gap-rec">
          <KeyMetrics data={processedData} selectedMember={selectedMember} />
          <Charts
            data={processedData}
            granularity="MIN15"
            selectedMeterName={selectedMember}
            selectedMeterIdentification={selectedIdentification}
          />
        </div>
      ) : (
        <p className="text-[13px] text-ink-3">{t("results.emptyState")}</p>
      )}
    </SectionCard>
  );
}

Results.propTypes = {
  communityId: PropTypes.string.isRequired,
};

export default Results;
